import { useMutation, useQuery } from "@tanstack/react-query"
import { RequestDocument } from "graphql-request/src/types"
import { AxiosRequestConfig } from "axios"
import { QueryObserverOptions, MutationObserverOptions } from "@tanstack/react-query"
import { graphqlClient, graphqlGetClient, httpClient } from "./query.clients"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"

/**
 * Audit the necessity of this file. after the AI era - ask AI if there is better way to do this.?
 */

/*
  Extends react-query with auth contexts
*/
export const useGraphqlQuery = <TData, TError = unknown, TQueryFnData = TData>(
  queryKey: string | any[],
  query: RequestDocument,
  method: "POST" | "GET" = "POST",
  variables?: Record<string, any>,
  reactQueryConfig?: QueryObserverOptions<TQueryFnData, TError, TData>,
  options?: {
    skipAuth?: boolean
  },
) => {
  return useQuery<TQueryFnData, TError, TData>({
    queryKey: typeof queryKey === "string" ? [queryKey] : queryKey,
    queryFn: () =>
      (method === "GET" ? graphqlGetClient : graphqlClient)[options?.skipAuth ? "request" : "refreshingRequest"](
        query,
        variables,
      ),
    refetchOnWindowFocus: false,
    ...reactQueryConfig,
  })
}

export const useGraphqlMutation = <
  TData,
  TVariables = Record<string, any> | void,
  TError = unknown,
  TContext = unknown,
>(
  mutationKey: string,
  mutation: RequestDocument,
  reactQueryConfig?: MutationObserverOptions<TData, TError, TVariables, TContext>,
  options?: { recaptcha: boolean },
) => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: [mutationKey],
    mutationFn: async (variables: TVariables) => {
      if (!options?.recaptcha) {
        return graphqlClient.refreshingRequest(mutation, variables)
      }
      if (!executeRecaptcha) {
        throw new Error("Google reCaptcha not yet available, please try again later.")
      }
      const token = await executeRecaptcha(mutationKey)
      return graphqlClient.refreshingRequest(mutation, variables, { recaptcha: token })
    },
    ...reactQueryConfig,
  })
}

export const useGet = (url: string, axiosConfig?: AxiosRequestConfig, reactQueryConfig?: QueryObserverOptions) => {
  return useQuery({
    queryKey: [`[GET] ${url}`],
    queryFn: () => httpClient.get(url as string, axiosConfig),
    refetchOnWindowFocus: false,
    ...reactQueryConfig,
  })
}

export const usePost = (
  url: string,
  data: Record<string, any> | undefined,
  axiosConfig?: AxiosRequestConfig,
  reactQueryConfig?: MutationObserverOptions,
) => {
  return useMutation({
    mutationKey: [`[POST] ${url}`],
    mutationFn: () => httpClient.post(url as string, data, axiosConfig),
    ...reactQueryConfig,
  })
}
